import { DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import { getTaxYear, getTaxYearOffset } from 'i2g-tax-officer';
import { Document710, DocumentSnippet700 } from 'i2g-underwood/lib/schema';
import {
  addManualTransaction,
  checkTaxConflicts,
  copyDocument,
  CopyDocumentOptions,
  CopyDocumentResult,
  EstimateStatus,
  evaluateSettingsForTaskList,
  getIndustryList,
  getOnboardingInvoice,
  getSampleDocument,
  getSubIndustryList,
  ManualTransaction,
  markDocumentSnippetsAsPaid,
  markEstimateAsDone,
  SupportedDocument,
  updateDocumentStates,
} from 'i2g-yakka';
import { fromJS, List, Map } from 'immutable';
import { Observable } from 'rxjs';
import { serialize } from '../../../../utils/helpers/model.helper';
import { BrowserManagerDataService } from '../../../browser-manager-data/browser-manager-data.service';
import { DateTimeService, WindowRefService } from 'i2g-ng-utils';
import {
  DocumentEmailStatesEnum,
  DocumentOverallStatesEnum,
  DocumentTypesEnum,
} from '../../../document-data/document-data.data';
import { Document as DocumentModel } from '../../../document-data/document.model';
import { DocumentPreview } from '../../../document-data/sub-models/document-preview.model';
import { LinkedDocumentPreview } from '../../../document-data/sub-models/linked-document-preview.model';
import { States } from '../../../document-data/sub-models/states.model';
import { EstimateStatusesEnum } from '../../../estimate-data/estimate-data.data';
import { Toggles } from '../../../features-data/sub-models/toggles.model';
import {
  CREDIT_MEMOS_MASTER_ROUTE,
  ESTIMATES_MASTER_ROUTE,
  INVOICES_MASTER_ROUTE,
  PURCHASE_ORDERS_MASTER_ROUTE,
} from '../../../main/main.data';
import { Settings } from '../../../settings-data/settings.model';
import { TaxYearStart } from '../../../settings-data/sub-models/company-settings/tax-year-start.model';
import { Tax } from '../../../settings-data/sub-models/company-settings/tax.model';
import { ImageBuilder } from '../../builders/image/image.builder';
import { timeout } from 'rxjs/operators';

const PRINT_FRAME_ID = 'print-frame';

export interface ICopyDocumentResult {
  updatedSourceProps: Map<string, any>;
  copiedDocumentProps: Map<string, any>;
}

@Injectable()
export class UtilsService {
  private _window: any;

  constructor(
    private _dateTimeService: DateTimeService,
    private _imageBuilder: ImageBuilder,
    private _browserManagerDataService: BrowserManagerDataService,
    @Inject(DOCUMENT) private _document: Document,
    windowRefService: WindowRefService
  ) {
    this._window = windowRefService.nativeWindow;
  }

  getDocumentMasterRoute(documentType: DocumentTypesEnum) {
    switch (documentType) {
      case DocumentTypesEnum.invoice:
        return INVOICES_MASTER_ROUTE;
      case DocumentTypesEnum.estimate:
        return ESTIMATES_MASTER_ROUTE;
      case DocumentTypesEnum.purchase_order:
        return PURCHASE_ORDERS_MASTER_ROUTE;
      case DocumentTypesEnum.credit_memo:
        return CREDIT_MEMOS_MASTER_ROUTE;
      default:
        throw new Error(
          `attempted to get document master route of document type: ${documentType}`
        );
    }
  }

  copyDocument(
    sourceDocument: DocumentModel,
    copyAsDocumentTemplate: DocumentModel,
    copyClient: boolean,
    copyItems: boolean,
    copyAttachments: boolean,
    copyTaxes: boolean,
    copyDepositRequest: boolean
  ): ICopyDocumentResult {
    const documentTemplateJson: SupportedDocument = serialize(
      copyAsDocumentTemplate
    ).toJS();
    const sourceDocumentJson: SupportedDocument = serialize(
      sourceDocument
    ).toJS();
    const {
      updated_source,
      copied_document,
    }: CopyDocumentResult = copyDocument(
      documentTemplateJson,
      sourceDocumentJson,
      {
        copyClient,
        copyItems,
        copyAttachments,
        copyTaxes,
        copyDepositRequest,
      } as CopyDocumentOptions
    );

    return {
      updatedSourceProps: fromJS(updated_source),
      copiedDocumentProps: fromJS(copied_document),
    };
  }

  checkTaxConflicts(
    accountTaxes: List<Tax>,
    documentToCopy: DocumentModel
  ): boolean {
    return checkTaxConflicts(accountTaxes.toJS(), documentToCopy.toJS());
  }

  updateDocumentStates(document: DocumentModel): SupportedDocument {
    return updateDocumentStates(serialize(document).toJS());
  }

  getSampleDocument(settings: Settings) {
    return getSampleDocument(settings.content.toJS());
  }

  openRenderableEntityWithUrl(pdfUrl: string): boolean {
    if (pdfUrl) {
      const printWindowRef = this._window.open(pdfUrl, 'printableDocumentPdf');

      // if popup blocker is enabled...
      if (!printWindowRef || printWindowRef.closed) {
        return false;
      } else {
        printWindowRef.focus();
        return true;
      }
    }
  }

  isPrintable(): boolean {
    const printFrame: HTMLIFrameElement = this._document.createElement(
      'iframe'
    );
    const printFrameId = 'testPrintFrame';

    if (
      this._browserManagerDataService.isIE() ||
      this._browserManagerDataService.isFirefox()
    ) {
      return false;
    }

    let result: boolean;

    printFrame.setAttribute('id', printFrameId);
    printFrame.setAttribute(
      'style',
      'position: absolute; display: none; left: -9999px;'
    );

    this._document.body.appendChild(printFrame);

    printFrame.focus();
    result = !!(printFrame.contentWindow && printFrame.contentWindow.print);

    this.removePrintFrame(printFrameId);

    return result;
  }

  printRenderableEntity(pdfBlob: Blob): Observable<void> {
    const printFrame: HTMLIFrameElement = this._document.createElement(
      'iframe'
    );
    const pdfUrl = this._imageBuilder.buildImageUrl(pdfBlob);

    this.removePrintFrame(PRINT_FRAME_ID);

    printFrame.setAttribute('id', PRINT_FRAME_ID);
    printFrame.setAttribute('src', pdfUrl);
    printFrame.setAttribute(
      'style',
      'position: absolute; display: none; left: -9999px;'
    );

    this._document.body.appendChild(printFrame);

    return Observable.create((obs) => {
      printFrame.onload = () => {
        this.printPdf(printFrame);

        obs.next();
        obs.complete();
      };
    }).pipe(timeout(5000));
  }

  printPdf(printFrame: HTMLIFrameElement) {
    // DOESN'T WORK FOR EDGE OR FIREFOX;
    // FOR IE: SEE https://app.raygun.com/crashreporting/ugqsmn/errors/1946333595/
    printFrame.focus();
    printFrame.contentWindow.print();
  }

  removePrintFrame(printFrameId: string) {
    const el = document.getElementById(printFrameId);

    if (el) {
      el.parentNode.removeChild(el);
    }
  }

  calculateDocState(
    documentStates: States,
    toggles: Toggles
  ): DocumentOverallStatesEnum {
    const overallState: DocumentOverallStatesEnum = documentStates.overall;
    const emailState: DocumentEmailStatesEnum = documentStates.email;

    if (
      overallState !== DocumentOverallStatesEnum.fully_paid &&
      documentStates.email === DocumentEmailStatesEnum.opened &&
      toggles.isAllAccess(Toggles.KEYS.EMAIL_TRACKING)
    ) {
      return DocumentOverallStatesEnum.opened;
    } else if (overallState === DocumentOverallStatesEnum.partially_paid) {
      return emailState as any;
    } else {
      return overallState;
    }
  }

  isDocumentOverdue(dueDate: string): boolean {
    const daysDifference: number = this._dateTimeService.calculateNumberOfUnitsFromDate(
      dueDate,
      this._dateTimeService.getDateString(),
      'day',
      'days',
      false
    );

    return daysDifference < 0;
  }

  markDocumentsAsPaid(documents: DocumentSnippet700[]): DocumentSnippet700[] {
    return markDocumentSnippetsAsPaid(documents);
  }

  markEstimatesAsDone(
    documents: Document710[],
    status: EstimateStatusesEnum
  ): Document710[] {
    return documents.map(
      (document) =>
        markEstimateAsDone(document, status as EstimateStatus) as Document710
    );
  }

  getTaxYear(taxYearStart: TaxYearStart, baseDate: string): number {
    return getTaxYear(
      getTaxYearOffset(taxYearStart.month - 1, taxYearStart.day),
      this._dateTimeService.convertToUtc(baseDate)
    );
  }

  isInTaxYear(
    taxYearStart: TaxYearStart,
    taxYear: number,
    baseDate: string
  ): boolean {
    return this.getTaxYear(taxYearStart, baseDate) === taxYear;
  }

  addManualTransaction(
    document: DocumentModel,
    transaction: ManualTransaction
  ) {
    return addManualTransaction(document.toJS(), transaction);
  }

  getConvertedDoc(document: DocumentModel): DocumentPreview {
    switch (document.documentType) {
      case DocumentTypesEnum.estimate:
        const linkedDocument: DocumentPreview = document.linkedDocumentPreviews
          .sortBy(
            (linkedDocumentPreview: LinkedDocumentPreview) =>
              -this._dateTimeService.getMilliseconds(
                linkedDocumentPreview.dateAdded,
                null
              )
          )
          .filter(
            (linkedDocumentPreview: LinkedDocumentPreview) =>
              linkedDocumentPreview.documentPreview.documentType ===
              DocumentTypesEnum.invoice
          )
          .getIn([0, LinkedDocumentPreview.KEYS.DOCUMENT], null);

        if (linkedDocument && !linkedDocument.isDeleted) {
          return linkedDocument;
        }

        return null;
      case DocumentTypesEnum.invoice:
        const sourceDocumentPreview = document.sourceDocumentPreview;

        if (
          sourceDocumentPreview &&
          sourceDocumentPreview.documentType === DocumentTypesEnum.estimate &&
          sourceDocumentPreview.isSynced
        ) {
          return sourceDocumentPreview;
        }

        return null;
      default:
        return null;
    }
  }

  getIndustries(
    locale: string,
    chosenKey: string = null
  ): { key: string; value: string }[] {
    return getIndustryList(locale, chosenKey).industries;
  }

  getSubIndustries(
    locale: string,
    chosenIndustryKey: string,
    chosenSubIndustryKeys: string[] = []
  ): { key: string; value: string }[] {
    return getSubIndustryList(locale, chosenIndustryKey, chosenSubIndustryKeys)
      .sub_industries;
  }

  getOnboardingInvoice(
    locale: string,
    companyName: string,
    email: string,
    industry: string
  ): Document710 {
    const onboardingInvoice = getOnboardingInvoice(
      locale,
      companyName,
      email,
      industry
    );

    // TODO: Remove this once i2g-underwood is updated to remove
    // client_financing_enabled property
    if (
      Object.prototype.hasOwnProperty.call(
        onboardingInvoice.content.settings,
        'client_financing_enabled'
      )
    ) {
      delete onboardingInvoice.content.settings.client_financing_enabled;
    }

    return onboardingInvoice;
  }

  checkSettingsForTaskList(previousSettings: Settings, newSettings: Settings) {
    return evaluateSettingsForTaskList(
      previousSettings.toJS(),
      newSettings.toJS()
    );
  }
}
